"use client"

import { useCallback, useRef, useState, useSyncExternalStore } from "react"

export type VoiceState = "idle" | "listening" | "denied"

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognition
    webkitSpeechRecognition?: new () => SpeechRecognition
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  start(): void
  stop(): void
  onresult: ((e: SpeechRecognitionEvent) => void) | null
  onerror: ((e: SpeechRecognitionErrorEvent) => void) | null
  onend: (() => void) | null
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message: string
}

// Errors that require user action (browser settings); not recoverable by retrying.
const PERMANENT_ERRORS = new Set(["not-allowed", "audio-capture"])

export function useVoiceInput(onTranscript: (text: string) => void) {
  const [state, setState] = useState<VoiceState>("idle")
  const recognitionRef = useRef<SpeechRecognition | null>(null)

  const start = useCallback(() => {
    if (typeof window === "undefined") return
    const SpeechRecognitionImpl = window.SpeechRecognition ?? window.webkitSpeechRecognition
    if (!SpeechRecognitionImpl) {
      setState("denied")
      return
    }

    const recognition = new SpeechRecognitionImpl()
    recognition.continuous = false
    recognition.interimResults = false
    recognition.lang = "en-US"

    // Closure flag so onend can distinguish a permission denial from a normal end.
    let permanent = false

    recognition.onresult = (e: SpeechRecognitionEvent) => {
      const transcript = e.results[0]?.[0]?.transcript ?? ""
      if (transcript) onTranscript(transcript)
      // onend fires next and returns state to idle.
    }

    recognition.onerror = (e: SpeechRecognitionErrorEvent) => {
      if (PERMANENT_ERRORS.has(e.error)) {
        permanent = true
        setState("denied")
      }
      // Transient errors (no-speech, network, …): onend returns to idle.
    }

    recognition.onend = () => {
      if (!permanent) setState("idle")
    }

    recognitionRef.current = recognition
    recognition.start()
    setState("listening")
  }, [onTranscript])

  const stop = useCallback(() => {
    recognitionRef.current?.stop()
    setState("idle")
  }, [])

  const supported = useSyncExternalStore(
    () => () => {},
    () =>
      typeof window.SpeechRecognition !== "undefined" ||
      typeof window.webkitSpeechRecognition !== "undefined",
    () => false,
  )

  return { state, start, stop, supported }
}
